/**
 * Daily.co video call panel — embeds the prebuilt Daily UI in an iframe.
 *
 * Daily enforces a global singleton across the page. We synchronously destroy
 * any existing instance before creating a new one, and we only forward
 * left-meeting events to the parent if join() actually succeeded — so that
 * connection failures (e.g. billing/plan issues) don't auto-redirect the
 * student to the feedback form.
 */
import React, { useEffect, useRef, useState } from "react";
import DailyIframe from "@daily-co/daily-js";

export default function VideoCall({ roomUrl, token, userName, onLeft }) {
  const containerRef = useRef(null);
  const callRef = useRef(null);
  const joinedRef = useRef(false);
  const onLeftRef = useRef(onLeft);
  onLeftRef.current = onLeft;

  const [error, setError] = useState(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Synchronously clear any existing global Daily instance.
    try {
      const existing = DailyIframe.getCallInstance && DailyIframe.getCallInstance();
      if (existing) existing.destroy();
    } catch { /* no-op */ }

    let call;
    try {
      call = DailyIframe.createFrame(containerRef.current, {
        iframeStyle: {
          width: "100%",
          height: "100%",
          border: "0",
          background: "#000",
        },
        showLeaveButton: true,
        showFullscreenButton: true,
        showLocalVideo: true,
        showParticipantsBar: false,
      });
    } catch (e) {
      setError(e?.message || String(e));
      return;
    }
    callRef.current = call;
    joinedRef.current = false;

    call.on("joined-meeting", () => {
      joinedRef.current = true;
    });
    call.on("left-meeting", () => {
      // Only treat as a real "leave" if we had actually joined.
      if (joinedRef.current) onLeftRef.current?.();
    });
    call.on("error", (ev) => {
      // eslint-disable-next-line no-console
      console.warn("Daily error event:", ev);
      setError(ev?.errorMsg || ev?.error || "Video call error");
    });

    call.join({ url: roomUrl, token, userName }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error("Daily join failed:", err);
      setError(err?.errorMsg || err?.message || "Could not join the call");
    });

    return () => {
      const c = callRef.current;
      callRef.current = null;
      joinedRef.current = false;
      try { c && c.destroy(); } catch { /* no-op */ }
    };
  }, [roomUrl, token, userName]);

  return (
    <div className="relative w-full h-full">
      <div
        ref={containerRef}
        data-testid="video-call-container"
        className="w-full h-full"
      />
      {error && (
        <div
          data-testid="video-call-error"
          className="absolute inset-0 flex items-center justify-center bg-black/85 text-white px-6 text-center pointer-events-none"
        >
          <div className="max-w-sm">
            <div className="text-[10px] tracking-[0.32em] uppercase opacity-60 mb-2">
              Video unavailable
            </div>
            <div className="text-sm opacity-85">{error}</div>
            <div className="mt-3 text-xs opacity-50">
              The whiteboard remains usable. Use the Leave button when you're done.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
